/*!
 * Pikaday
 *
 * Copyright © 2014 David Bushell | BSD & MIT license | https://github.com/dbushell/Pikaday
 */

(function (root, factory) {
    'use strict';

    var moment;
    if (typeof exports === 'object') {
        // CommonJS module
        // Load moment.js as an optional dependency
        try {
            moment = require('moment');
        } catch (e) {}
        module.exports = factory(moment);
    } else if (typeof define === 'function' && define.amd) {
        // AMD. Register as an anonymous module.
        define(function (req) {
            // Load moment.js as an optional dependency
            var id = 'moment';
            try {
                moment = req(id);
            } catch (e) {}
            return factory(moment);
        });
    } else {
        root.Pikaday = factory(root.moment);
    }
})(this, function (moment) {
    'use strict';

    /**
     * feature detection and helper functions
     */
    var hasMoment = typeof moment === 'function',
        hasEventListeners = !!window.addEventListener,
        document = window.document,
        sto = window.setTimeout,
        addEvent = function (el, e, callback, capture) {
            if (hasEventListeners) {
                el.addEventListener(e, callback, !!capture);
            } else {
                el.attachEvent('on' + e, callback);
            }
        },
        removeEvent = function (el, e, callback, capture) {
            if (hasEventListeners) {
                el.removeEventListener(e, callback, !!capture);
            } else {
                el.detachEvent('on' + e, callback);
            }
        },
        trim = function (str) {
            return str.trim ? str.trim() : str.replace(/^\s+|\s+$/g, '');
        },
        hasClass = function (el, cn) {
            return (' ' + el.className + ' ').indexOf(' ' + cn + ' ') !== -1;
        },
        addClass = function (el, cn) {
            if (!hasClass(el, cn)) {
                el.className = el.className === '' ? cn : el.className + ' ' + cn;
            }
        },
        removeClass = function (el, cn) {
            el.className = trim((' ' + el.className + ' ').replace(' ' + cn + ' ', ' '));
        },
        isArray = function (obj) {
            return /Array/.test(Object.prototype.toString.call(obj));
        },
        isDate = function (obj) {
            return /Date/.test(Object.prototype.toString.call(obj)) && !isNaN(obj.getTime());
        },
        isWeekend = function (date) {
            var day = date.getDay();
            return day === 0 || day === 6;
        },
        isLeapYear = function (year) {
            // solution by Matti Virkkunen: http://stackoverflow.com/a/4881951
            return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
        },
        getDaysInMonth = function (year, month) {
            return [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month];
        },
        setToStartOfDay = function (date) {
            if (isDate(date)) date.setHours(0, 0, 0, 0);
        },
        compareDates = function (a, b) {
            // weak date comparison (use setToStartOfDay(date) to ensure correct result)
            return a.getTime() === b.getTime();
        },
        extend = function (to, from, overwrite) {
            var prop, hasProp;
            for (prop in from) {
                hasProp = to[prop] !== undefined;
                if (
                    hasProp &&
                    typeof from[prop] === 'object' &&
                    from[prop] !== null &&
                    from[prop].nodeName === undefined
                ) {
                    if (isDate(from[prop])) {
                        if (overwrite) {
                            to[prop] = new Date(from[prop].getTime());
                        }
                    } else if (isArray(from[prop])) {
                        if (overwrite) {
                            to[prop] = from[prop].slice(0);
                        }
                    } else {
                        to[prop] = extend({}, from[prop], overwrite);
                    }
                } else if (overwrite || !hasProp) {
                    to[prop] = from[prop];
                }
            }
            return to;
        },
        fireEvent = function (el, eventName, data) {
            var ev;

            if (document.createEvent) {
                ev = document.createEvent('HTMLEvents');
                ev.initEvent(eventName, true, false);
                ev = extend(ev, data);
                el.dispatchEvent(ev);
            } else if (document.createEventObject) {
                ev = document.createEventObject();
                ev = extend(ev, data);
                el.fireEvent('on' + eventName, ev);
            }
        },
        adjustCalendar = function (calendar) {
            if (calendar.month < 0) {
                calendar.year -= Math.ceil(Math.abs(calendar.month) / 12);
                calendar.month += 12;
            }
            if (calendar.month > 11) {
                calendar.year += Math.floor(Math.abs(calendar.month) / 12);
                calendar.month -= 12;
            }
            return calendar;
        },
        /**
         * defaults and localisation
         */
        defaults = {
            // bind the picker to a form field
            field: null,

            // automatically show/hide the picker on `field` focus (default `true` if `field` is set)
            bound: undefined,

            // data-attribute on the input field with an aria assistance tekst (only applied when `bound` is set)
            ariaLabel: 'Use the arrow keys to pick a date',

            // position of the datepicker, relative to the field (default to bottom & left)
            // ('bottom' & 'left' keywords are not used, 'top' & 'right' are modifier on the bottom/left position)
            position: 'bottom left',

            // automatically fit in the viewport even if it means repositioning from the position option
            reposition: true,

            // the default output format for `.toString()` and `field` value
            format: 'YYYY-MM-DD',

            // the toString function which gets passed a current date object and format
            // and returns a string
            toString: null,

            // used to create date object from current input string
            parse: null,

            // the initial date to view when first opened
            defaultDate: null,

            // make the `defaultDate` the initial selected value
            setDefaultDate: false,

            // first day of week (0: Sunday, 1: Monday etc)
            firstDay: 0,

            // the default flag for moment's strict date parsing
            formatStrict: false,

            // the minimum/earliest date that can be selected
            minDate: null,
            // the maximum/latest date that can be selected
            maxDate: null,

            firstAvailableDate: null,
            lastAvailableDate: null,
            // Array<Date>
            disabledDates: [],

            // number of years either side, or array of upper/lower range
            yearRange: 10,

            // show week numbers at head of row
            showWeekNumber: false,

            // Week picker mode
            pickWholeWeek: false,

            // used internally (don't config outside)
            minYear: 0,
            maxYear: 9999,
            minMonth: undefined,
            maxMonth: undefined,

            startRange: null,
            endRange: null,

            isRTL: false,

            // Additional text to append to the year in the calendar title
            yearSuffix: '',

            // Render the month after year in the calendar title
            showMonthAfterYear: false,

            // Render days of the calendar grid that fall in the next or previous month
            showDaysInNextAndPreviousMonths: false,

            // Allows user to select days that fall in the next or previous month
            enableSelectionDaysInNextAndPreviousMonths: false,

            // how many months are visible
            numberOfMonths: 1,

            // when numberOfMonths is used, this will help you to choose where the main calendar will be (default `left`, can be set to `right`)
            // only used for the first display or when a selected date is not visible
            mainCalendar: 'left',

            // Specify a DOM element to render the calendar in
            container: undefined,

            // Blur field when date is selected
            blurFieldOnSelect: true,

            // internationalization
            i18n: {
                previousMonth: 'Previous Month',
                nextMonth: 'Next Month',
                months: [
                    'January',
                    'February',
                    'March',
                    'April',
                    'May',
                    'June',
                    'July',
                    'August',
                    'September',
                    'October',
                    'November',
                    'December',
                ],
                weekdays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
                weekdaysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
            },

            // Theme Classname
            theme: null,

            // events array
            events: [],

            // callback function
            onSelect: null,
            onOpen: null,
            onClose: null,
            onDraw: null,

            // Enable keyboard input
            keyboardInput: true,
        },
        /**
         * templating functions to abstract HTML rendering
         */
        renderDayName = function (opts, day, abbr) {
            day += opts.firstDay;
            while (day >= 7) {
                day -= 7;
            }
            return abbr ? opts.i18n.weekdaysShort[day] : opts.i18n.weekdays[day];
        },
        /**
         * This function determines the day of the week for a given date using Zeller's Congruence algorithm.
         * The returned value ranges from 0 (Sunday) to 6 (Saturday).
         */
        renderWeekdayFromDate = function (opts, abbr = false) {
            let day = opts.day;
            let month = opts.month + 1;
            let year = opts.year;

            if (month < 3) {
                --year;
                month += 10;
            } else {
                month -= 2;
            }
            let dayOfWeek =
                Math.floor(
                    day +
                        Math.floor((31 * month) / 12) +
                        year +
                        Math.floor(year / 4) -
                        Math.floor(year / 100) +
                        Math.floor(year / 400),
                ) % 7;

            return abbr ? opts.i18n.weekdaysShort[dayOfWeek] : opts.i18n.weekdays[dayOfWeek];
        },
        extractDateFromPikaButton = function (button) {
            try {
                let year = button.getAttribute('data-pika-year');
                let month = button.getAttribute('data-pika-month');
                let day = button.getAttribute('data-pika-day');
                return new Date(year, month, day);
            } catch (e) {
                return new Date();
            }
        },
        normalizeKey = function (e) {
            var legacyKeys = {
                33: 'PageUp',
                34: 'PageDown',
                35: 'End',
                36: 'Home',
                37: 'ArrowLeft',
                38: 'ArrowUp',
                39: 'ArrowRight',
                40: 'ArrowDown',
            };
            var aliases = {
                Left: 'ArrowLeft',
                Up: 'ArrowUp',
                Right: 'ArrowRight',
                Down: 'ArrowDown',
                // alte Browser (IE/Edge Legacy) senden noch diese e.key-Werte
                Spacebar: ' ',
                Esc: 'Escape',
            };

            if (!e.key) {
                return legacyKeys[e.keyCode] || '';
            }

            return aliases[e.key] || e.key;
        },
        renderDay = function (opts) {
            var arr = [];
            let cellState = '';
            let buttonState = '';
            var disabled = '';
            var tabindex = -1;
            var title = '';
            if (opts.isEmpty) {
                if (opts.showDaysInNextAndPreviousMonths) {
                    arr.push('is-outside-current-month');

                    if (!opts.enableSelectionDaysInNextAndPreviousMonths) {
                        arr.push('is-selection-disabled');
                    }
                } else {
                    return '<td role="gridcell" class="is-empty"></td>';
                }
            }
            if (opts.isDisabled) {
                arr.push('is-disabled');
                if (opts.isWeekend) {
                    arr.push('is-weekend');
                }

                if (opts.isInPast) {
                    arr.push('is-in-past');
                }

                disabled = 'aria-disabled="true"';
                title =
                    opts.self.i18n.titles[14] +
                    ' ' +
                    opts.day +
                    '. ' +
                    opts.self.i18n.months[opts.month] +
                    ', ' +
                    opts.year;
            } else {
                title =
                    opts.self.i18n.titles[15] +
                    ' ' +
                    opts.day +
                    '. ' +
                    opts.self.i18n.months[opts.month] +
                    ', ' +
                    opts.year;
            }

            if (opts.isToday) {
                arr.push('is-today');
                cellState += ' aria-current="date"';
                buttonState += ' aria-current="date"';
            }
            if (opts.isSelected) {
                arr.push('is-selected');
                cellState += ' aria-selected="true"';
                buttonState += ' aria-pressed="true"';
            }
            if (opts.isFocus) {
                arr.push('is-focus');
            }
            if (opts.hasEvent) {
                arr.push('has-event');
            }
            if (opts.isInRange) {
                arr.push('is-inrange');
            }
            if (opts.isStartRange) {
                arr.push('is-startrange');
            }
            if (opts.isEndRange) {
                arr.push('is-endrange');
            }

            var dom =
                '<td ' +
                'role="gridcell"' +
                cellState +
                ' data-day="' +
                opts.day +
                '" ' +
                'class="' +
                arr.join(' ') +
                '" ' +
                'headers="header-' +
                renderWeekdayFromDate(opts, false) +
                '">' +
                '<button ' +
                'tabindex="' +
                tabindex +
                '"  ' +
                'aria-label="' +
                title +
                '" ' +
                'class="pika-button pika-day" ' +
                disabled +
                buttonState +
                ' ' +
                'data-pika-year="' +
                opts.year +
                '" ' +
                'data-pika-month="' +
                opts.month +
                '" ' +
                'data-pika-day="' +
                opts.day +
                '" ' +
                '>' +
                opts.day +
                getFAIcon(arr) +
                '</button>' +
                '</td>';
            return dom;
        },
        getFAIcon = function (dayState) {
            let check =
                '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" viewBox="0 0 448 512"><path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/></svg>';
            let star =
                '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" viewBox="0 0 576 512"><path d="M316.9 18C311.6 7 300.4 0 288.1 0s-23.4 7-28.8 18L195 150.3 51.4 171.5c-12 1.8-22 10.2-25.7 21.7s-.7 24.2 7.9 32.7L137.8 329 113.2 474.7c-2 12 3 24.2 12.9 31.3s23 8 33.8 2.3l128.3-68.5 128.3 68.5c10.8 5.7 23.9 4.9 33.8-2.3s14.9-19.3 12.9-31.3L438.5 329 542.7 225.9c8.6-8.5 11.7-21.2 7.9-32.7s-13.7-19.9-25.7-21.7L381.2 150.3 316.9 18z"/></svg>';
            let squareFilled =
                '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" viewBox="0 0 448 512"><path d="M0 96C0 60.7 28.7 32 64 32H384c35.3 0 64 28.7 64 64V416c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V96z"/></svg>';
            let cross =
                '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" viewBox="0 0 384 512"><path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"/></svg>';

            let returnVal = check;
            if (dayState.includes('is-weekend')) {
                returnVal = cross;
            } else if (dayState.includes('is-disabled')) {
                returnVal = squareFilled;
            } else if (dayState.includes('is-selected')) {
                returnVal = star;
            }
            return returnVal;
        },
        renderWeek = function (d, m, y) {
            // Lifted from http://javascript.about.com/library/blweekyear.htm, lightly modified.
            var onejan = new Date(y, 0, 1),
                weekNum = Math.ceil(((new Date(y, m, d) - onejan) / 86400000 + onejan.getDay() + 1) / 7);
            return '<td class="pika-week">' + weekNum + '</td>';
        },
        renderRow = function (days, isRTL, pickWholeWeek, isRowSelected) {
            return (
                '<tr role="row" class="pika-row' +
                (pickWholeWeek ? ' pick-whole-week' : '') +
                (isRowSelected ? ' is-selected' : '') +
                '">' +
                (isRTL ? days.reverse() : days).join('') +
                '</tr>'
            );
        },
        renderBody = function (rows) {
            return '<tbody>' + rows.join('') + '</tbody>';
        },
        renderHead = function (opts) {
            let i,
                arr = [];
            if (opts.showWeekNumber) {
                arr.push('<th role="columnheader"></th>');
            }
            for (i = 0; i < 7; i++) {
                arr.push(
                    '<th role="columnheader" scope="col" id="header-' +
                        renderDayName(opts, i) +
                        '"><abbr title="' +
                        renderDayName(opts, i) +
                        '">' +
                        renderDayName(opts, i, true) +
                        '</abbr></th>',
                );
            }
            return '<thead><tr role="row">' + (opts.isRTL ? arr.reverse() : arr).join('') + '</tr></thead>';
        },
        renderCaption = function (opts, rows, columns) {
            const tableCaption = opts.i18n.caption.replace('[[%0%]]', columns).replace('[[%1%]]', rows);
            return '<caption id="table-description" class="visually-hidden">' + tableCaption + '</caption>';
        },
        renderTitle = function (instance, c, year, month, refYear, randId) {
            var i,
                j,
                arr,
                opts = instance._o,
                isMinYear = year === opts.minYear,
                isMaxYear = year === opts.maxYear,
                html = '<div id="' + randId + '" class="pika-title" >',
                monthHtml,
                yearHtml,
                prev = true,
                next = true;

            for (arr = [], i = 0; i < 12; i++) {
                arr.push(
                    '<option value="' +
                        (year === refYear ? i - c : 12 + i - c) +
                        '"' +
                        (i === month ? ' selected="selected"' : '') +
                        ((isMinYear && i < opts.minMonth) || (isMaxYear && i > opts.maxMonth)
                            ? 'disabled="disabled"'
                            : '') +
                        '>' +
                        opts.i18n.months[i] +
                        '</option>',
                );
            }

            monthHtml =
                '<div class="pika-label"><select tabindex="0" class="pika-select pika-select-month" aria-label="' +
                opts.i18n.labels[0] +
                '">' +
                arr.join('') +
                '</select><p>' +
                opts.i18n.months[month] +
                '</p></div>';

            if (isArray(opts.yearRange)) {
                i = opts.yearRange[0];
                j = opts.yearRange[1] + 1;
            } else {
                i = year - opts.yearRange;
                j = 1 + year + opts.yearRange;
            }

            for (arr = []; i < j && i <= opts.maxYear; i++) {
                if (i >= opts.minYear) {
                    arr.push(
                        '<option value="' +
                            i +
                            '"' +
                            (i === year ? ' selected="selected"' : '') +
                            '>' +
                            i +
                            '</option>',
                    );
                }
            }

            if (isMinYear && (month === 0 || opts.minMonth >= month)) {
                prev = false;
            }

            if (isMaxYear && (month === 11 || opts.maxMonth <= month)) {
                next = false;
            }

            const pikaSelectContainerHtml = '<div class="pika-month-select-container">';

            /* Top Month + Year Navigation START */
            yearHtml =
                '<div class="pika-label"><select tabindex="0" class="pika-select pika-select-year" aria-label="' +
                opts.i18n.labels[1] +
                '">' +
                arr.join('') +
                '</select><p>' +
                year +
                opts.yearSuffix +
                '</p></div>';
            html += pikaSelectContainerHtml;
            if (c === 0) {
                html +=
                    '<button ' +
                    'class="pika-prev' +
                    (prev ? '' : ' is-disabled') +
                    '" ' +
                    (prev ? '' : "disabled='disabled'") +
                    (prev ? '' : "aria-disabled='true'") +
                    'type="button" ' +
                    'aria-label="' +
                    opts.i18n.previousMonth +
                    '" ' +
                    'title="' +
                    opts.i18n.previousMonth +
                    '">‹</button>';
            }

            if (opts.showMonthAfterYear) {
                html += '<div class="d-flex">' + yearHtml + monthHtml + '</div>';
            } else {
                html += '<div class="d-flex">' + monthHtml + yearHtml + '</div>';
            }

            if (c === instance._o.numberOfMonths - 1) {
                html +=
                    '<button ' +
                    'class="pika-next' +
                    (next ? '' : ' is-disabled') +
                    '" ' +
                    'type="button" ' +
                    'aria-label="' +
                    opts.i18n.nextMonth +
                    '" ' +
                    (next ? '' : "disabled='disabled'") +
                    (next ? '' : "aria-disabled='true'") +
                    'title="' +
                    opts.i18n.nextMonth +
                    '">›</button>';
            }
            html += '</div>';
            /* Top Month + Year Navigation END */

            html += pikaSelectContainerHtml;
            if (c === 0) {
                html +=
                    '<div></div><button class="pika-today" id="pika-today" type="button" aria-label="' +
                    opts.i18n.today +
                    ' " title="' +
                    opts.i18n.today +
                    '">' +
                    opts.i18n.today +
                    '</button><div></div>';
            }
            html += '</div>';

            return html + '</div>';
        },
        renderTable = function (opts, data, randId) {
            return (
                '<table class="pika-table" role="grid">' +
                renderCaption(opts, data.length, 7) +
                renderHead(opts) +
                renderBody(data) +
                '</table>'
            );
        },
        /**
         * Pikaday constructor
         */
        Pikaday = function (options) {
            var self = this,
                opts = self.config(options);
            self._onActivate = function (e) {
                if (!self._v) {
                    return;
                }
                e = e || window.event;
                var target = e.target || e.srcElement;
                if (!target) {
                    return;
                }
                if (target.closest) {
                    target = target.closest('.pika-button') || target;
                }

                if (!hasClass(target, 'is-disabled')) {
                    if (
                        hasClass(target, 'pika-button') &&
                        !hasClass(target, 'is-empty') &&
                        !hasClass(target.parentNode, 'is-disabled')
                    ) {
                        self.setDate(
                            new Date(
                                target.getAttribute('data-pika-year'),
                                target.getAttribute('data-pika-month'),
                                target.getAttribute('data-pika-day'),
                            ),
                        );
                        if (opts.bound) {
                            sto(function () {
                                self.hide();
                                if (opts.blurFieldOnSelect && opts.field) {
                                    opts.field.blur();
                                }
                            }, 100);
                        }
                    } else if (hasClass(target, 'pika-prev')) {
                        self.prevMonth();
                    } else if (hasClass(target, 'pika-next')) {
                        self.nextMonth();
                    } else if (hasClass(target, 'pika-today')) {
                        if (new Date().getTime() < self._o.firstAvailableDate.getTime()) {
                            self.setDate(self._o.firstAvailableDate);
                        } else {
                            self.setDate(new Date());
                        }
                        // self.setDate(self.firstAvailableDate);
                    }
                }
                if (!hasClass(target, 'pika-select')) {
                    // if this is touch event prevent mouse events emulation
                    if (e.preventDefault) {
                        e.preventDefault();
                    } else {
                        e.returnValue = false;
                        return false;
                    }
                } else {
                    self._c = true;
                }
            };

            self._onClickToday = function (e) {
                self.setDate(new Date());
            };

            self._onChange = function (e) {
                e = e || window.event;
                var target = e.target || e.srcElement;
                if (!target) {
                    return;
                }
                if (hasClass(target, 'pika-select-month')) {
                    self.gotoMonth(target.value);
                } else if (hasClass(target, 'pika-select-year')) {
                    self.gotoYear(target.value);
                }
            };

            self._onFocusIn = function (e) {
                e = e || window.event;
                var target = e.target;
                if (!target || !target.classList || !target.classList.contains('pika-day')) {
                    return;
                }

                var date = extractDateFromPikaButton(target);
                setToStartOfDay(date);
                self._dFocus = date;
            };

            self._onKeyDown = function (e) {
                e = e || window.event;

                if (!self.isVisible()) {
                    return;
                }

                var active = self.getActiveElement();
                if (!active || !active.classList || !active.classList.contains('pika-day')) {
                    return;
                }

                var handled = true;

                switch (normalizeKey(e)) {
                    case 'ArrowLeft':
                        self.adjustDate('subtract', 1);
                        break;
                    case 'ArrowRight':
                        self.adjustDate('add', 1);
                        break;
                    case 'ArrowUp':
                        self.adjustDate('subtract', 7);
                        break;
                    case 'ArrowDown':
                        self.adjustDate('add', 7);
                        break;
                    case 'Home':
                        self.adjustDate('subtract', self.getWeekdayOffset());
                        break;
                    case 'End':
                        self.adjustDate('add', 6 - self.getWeekdayOffset());
                        break;
                    case 'PageUp':
                        self.adjustMonth('subtract');
                        break;
                    case 'PageDown':
                        self.adjustMonth('add');
                        break;
                    case 'Escape':
                        if (opts.bound) {
                            self.hide();
                            if (opts.field) {
                                removeEvent(opts.trigger, 'focus', self._onInputFocus);
                                opts.field.focus();
                                addEvent(opts.trigger, 'focus', self._onInputFocus);
                            }
                        } else {
                            handled = false;
                        }
                        break;
                    default:
                        handled = false;
                }

                if (handled) {
                    e.preventDefault();
                }
            };

            self._onInputChange = function (e) {
                var date;

                if (e.firedBy === self) {
                    return;
                }
                if (opts.parse) {
                    date = opts.parse(opts.field.value, opts.format);
                } else if (hasMoment) {
                    date = moment(opts.field.value, opts.format, opts.formatStrict);
                    date = date && date.isValid() ? date.toDate() : null;
                } else {
                    date = new Date(Date.parse(opts.field.value));
                }
                if (isDate(date)) {
                    self.setDate(date);
                }
                if (!self._v) {
                    self.show();
                }
            };

            self._onInputFocus = function () {
                self.show();
            };

            self._onInputClick = function () {
                self.show();
            };

            self._onInputBlur = function () {
                // IE allows pika div to gain focus; catch blur the input field
                var pEl = self.getActiveElement();
                do {
                    if (hasClass(pEl, 'pika-single')) {
                        return;
                    }
                } while ((pEl = pEl.parentNode));

                if (!self._c) {
                    self._b = sto(function () {
                        self.hide();
                    }, 50);
                }
                self._c = false;
            };

            self._onClick = function (e) {
                e = e || window.event;
                var target = e.target || e.srcElement,
                    pEl = target;
                if (!target) {
                    return;
                }
                if (!hasEventListeners && hasClass(target, 'pika-select')) {
                    if (!target.onchange) {
                        target.setAttribute('onchange', 'return;');
                        addEvent(target, 'change', self._onChange);
                    }
                }
                do {
                    if (hasClass(pEl, 'pika-single') || pEl === opts.trigger) {
                        return;
                    }
                } while ((pEl = pEl.parentNode));
                if (self._v && target !== opts.trigger && pEl !== opts.trigger) {
                    self.hide();
                }
            };

            self.el = document.createElement('div');
            addEvent(self.el, 'click', self._onActivate, true);
            self.el.className = 'pika-single' + (opts.isRTL ? ' is-rtl' : '');

            addEvent(self.el, 'change', self._onChange);
            addEvent(self.el, 'focusin', self._onFocusIn, true);

            // addEvent(self._o.field, 'change', self._onChange);

            if (opts.keyboardInput) {
                addEvent(self.el, 'keydown', self._onKeyDown, true);
            }

            if (opts.field) {
                if (opts.container) {
                    opts.container.appendChild(self.el);
                } else if (opts.bound) {
                    document.body.appendChild(self.el);
                } else {
                    opts.field.parentNode.insertBefore(self.el, opts.field.nextSibling);
                }
                addEvent(opts.field, 'change', self._onInputChange);

                if (!opts.defaultDate) {
                    if (hasMoment && opts.field.value) {
                        opts.defaultDate = moment(opts.field.value, opts.format).toDate();
                    } else {
                        opts.defaultDate = new Date(Date.parse(opts.field.value));
                    }
                    opts.setDefaultDate = true;
                }
            }

            var defDate = opts.defaultDate;

            if (isDate(defDate)) {
                if (opts.setDefaultDate) {
                    // preventonselect: calls onselect event on init
                    self.setDate(defDate, false);
                } else {
                    self.gotoDate(defDate);
                }
            } else {
                self.gotoDate(new Date());
            }

            if (opts.bound) {
                this.hide();
                self.el.className += ' is-bound';
                addEvent(opts.trigger, 'click', self._onInputClick);
                addEvent(opts.trigger, 'focus', self._onInputFocus);
                addEvent(opts.trigger, 'blur', self._onInputBlur);
            } else {
                this.show();
            }
        };

    /**
     * public Pikaday API
     */
    Pikaday.prototype = {
        /**
         * configure functionality
         */
        config: function (options) {
            if (!this._o) {
                this._o = extend({}, defaults, true);
            }

            var opts = extend(this._o, options, true);

            opts.isRTL = !!opts.isRTL;

            opts.showLegend = !!opts.showLegend;

            opts.field = opts.field && opts.field.nodeName ? opts.field : null;

            opts.theme = typeof opts.theme === 'string' && opts.theme ? opts.theme : null;

            opts.bound = !!(opts.bound !== undefined ? opts.field && opts.bound : opts.field);

            opts.trigger = opts.trigger && opts.trigger.nodeName ? opts.trigger : opts.field;

            opts.disableWeekends = !!opts.disableWeekends;

            opts.disableDayFn = typeof opts.disableDayFn === 'function' ? opts.disableDayFn : null;

            var nom = parseInt(opts.numberOfMonths, 10) || 1;
            opts.numberOfMonths = nom > 4 ? 4 : nom;

            if (!isDate(opts.minDate)) {
                opts.minDate = false;
            }
            if (!isDate(opts.maxDate)) {
                opts.maxDate = false;
            }

            if (opts.minDate && opts.maxDate && opts.maxDate < opts.minDate) {
                opts.maxDate = opts.minDate = false;
            }
            if (opts.minDate) {
                setToStartOfDay(opts.minDate);
                opts.minYear = opts.minDate.getFullYear();
                opts.minMonth = opts.minDate.getMonth();

                opts.firstAvailableDate = new Date(
                    opts.minDate.getFullYear(),
                    opts.minDate.getMonth(),
                    opts.minDate.getDate(),
                );
                for (var i = 0; i < opts.disabledDates.length; i++) {
                    if (opts.disabledDates[i].toString() == opts.firstAvailableDate.toString()) {
                        opts.firstAvailableDate.setDate(opts.firstAvailableDate.getDate() + 1);
                    } else {
                        break;
                    }
                }
            }
            if (opts.maxDate) {
                this.setMaxDate(opts.maxDate);

                opts.lastAvailableDate = new Date(
                    opts.maxDate.getFullYear(),
                    opts.maxDate.getMonth(),
                    opts.maxDate.getDate(),
                );
                for (var i = opts.disabledDates.length - 1; i > 0; i--) {
                    if (opts.disabledDates[i].toString() == opts.lastAvailableDate.toString()) {
                        opts.lastAvailableDate.setDate(opts.lastAvailableDate.getDate() - 1);
                    } else {
                        break;
                    }
                }
            }

            opts.disabledDates = opts.disabledDates.map(function (res) {
                var d = new Date(res);
                setToStartOfDay(d);
                return d;
            });

            if (isArray(opts.yearRange)) {
                var fallback = new Date().getFullYear() - 10;
                opts.yearRange[0] = parseInt(opts.yearRange[0], 10) || fallback;
                opts.yearRange[1] = parseInt(opts.yearRange[1], 10) || fallback;
            } else {
                opts.yearRange = Math.abs(parseInt(opts.yearRange, 10)) || defaults.yearRange;
                if (opts.yearRange > 100) {
                    opts.yearRange = 100;
                }
            }

            return opts;
        },

        /**
         * return a formatted string of the current selection (using Moment.js if available)
         */
        toString: function (format) {
            format = format || this._o.format;
            if (!isDate(this._d)) {
                return '';
            }
            if (this._o.toString) {
                return this._o.toString(this._d, format);
            }
            if (hasMoment) {
                return moment(this._d).format(format);
            }
            return this._d.toDateString();
        },

        /**
         * return a Moment.js object of the current selection (if available)
         */
        getMoment: function () {
            return hasMoment ? moment(this._d) : null;
        },

        /**
         * set the current selection from a Moment.js object (if available)
         */
        setMoment: function (date, preventOnSelect) {
            if (hasMoment && moment.isMoment(date)) {
                this.setDate(date.toDate(), preventOnSelect);
            }
        },

        /**
         * return a Date object of the current selection
         */
        getDate: function () {
            return isDate(this._d) ? new Date(this._d.getTime()) : null;
        },

        /**
         * set the current selection
         */
        setDate: function (date, preventOnSelect) {
            if (!date) {
                this._d = null;

                if (this._o.field) {
                    this._o.field.value = '';
                    fireEvent(this._o.field, 'change', { firedBy: this });
                }

                return this.draw();
            }
            if (typeof date === 'string') {
                date = new Date(Date.parse(date));
            }
            if (!isDate(date)) {
                return;
            }

            var min = this._o.firstAvailableDate || this._o.minDate,
                max = this._o.lastAvailableDate || this._o.maxDate;

            if (isDate(min) && date < min) {
                date = min;
            } else if (isDate(max) && date > max) {
                date = max;
            }

            this._d = new Date(date.getTime());
            this._dFocus = null;
            setToStartOfDay(this._d);
            this.gotoDate(this._d);

            if (!preventOnSelect && typeof this._o.onSelect === 'function') {
                this._o.onSelect.call(this, this.getDate());
            }

            if (this._o.field) {
                this._o.field.value = this.toString();
                fireEvent(this._o.field, 'change', { firedBy: this });
            }
        },

        /**
         * set focus to date
         * @param date
         */
        setFocus: function (date) {
            var min = this._o.firstAvailableDate || this._o.minDate,
                max = this._o.lastAvailableDate || this._o.maxDate;

            if (isDate(min) && date < min) {
                date = min;
            } else if (isDate(max) && date > max) {
                date = max;
            }

            this._dFocus = new Date(date.getTime());
            setToStartOfDay(this._dFocus);
            this.gotoFocusDate(this._dFocus);

            if (this._o.field) {
                this._o.field.value = this.toString();
                fireEvent(this._o.field, 'change', { firedBy: this });
            }
        },

        /**
         * change view to a specific focus date
         */
        gotoFocusDate: function (date) {
            var newCalendar = true;

            if (!isDate(date)) {
                return;
            }

            if (this.calendars) {
                var firstVisibleDate = new Date(this.calendars[0].year, this.calendars[0].month, 1),
                    lastVisibleDate = new Date(
                        this.calendars[this.calendars.length - 1].year,
                        this.calendars[this.calendars.length - 1].month,
                        1,
                    ),
                    visibleDate = date.getTime();
                // get the end of the month
                lastVisibleDate.setMonth(lastVisibleDate.getMonth() + 1);
                lastVisibleDate.setDate(lastVisibleDate.getDate() - 1);
                newCalendar = visibleDate < firstVisibleDate.getTime() || lastVisibleDate.getTime() < visibleDate;
            }

            if (newCalendar) {
                this.calendars = [
                    {
                        month: date.getMonth(),
                        year: date.getFullYear(),
                    },
                ];
                if (this._o.mainCalendar === 'right') {
                    this.calendars[0].month += 1 - this._o.numberOfMonths;
                }
            }

            this.adjustCalendars();
            var el = this.el.querySelector('.pika-row > .is-focus > button');
            if (el) {
                el.focus();
            }
        },

        /**
         * change view to a specific date
         */
        gotoDate: function (date) {
            var newCalendar = true;

            if (!isDate(date)) {
                return;
            }

            if (this.calendars) {
                var firstVisibleDate = new Date(this.calendars[0].year, this.calendars[0].month, 1),
                    lastVisibleDate = new Date(
                        this.calendars[this.calendars.length - 1].year,
                        this.calendars[this.calendars.length - 1].month,
                        1,
                    ),
                    visibleDate = date.getTime();
                // get the end of the month
                lastVisibleDate.setMonth(lastVisibleDate.getMonth() + 1);
                lastVisibleDate.setDate(lastVisibleDate.getDate() - 1);
                newCalendar = visibleDate < firstVisibleDate.getTime() || lastVisibleDate.getTime() < visibleDate;
            }

            if (newCalendar) {
                this.calendars = [
                    {
                        month: date.getMonth(),
                        year: date.getFullYear(),
                    },
                ];
                if (this._o.mainCalendar === 'right') {
                    this.calendars[0].month += 1 - this._o.numberOfMonths;
                }
            }

            this.adjustCalendars();
        },

        dayIsDisabled: function (date) {},

        /**
         * controls the focus
         * @param sign
         * @param days
         */
        adjustDate: function (sign, days) {
            var day = this._dFocus || this.getDate() || new Date();
            var difference = parseInt(days) * 24 * 60 * 60 * 1000;
            var newDay;

            // preventing an infinite loop if all days are disabled
            for (var i = this.config().numberOfMonths * 31 + 31; i >= 0; i--) {
                if (sign === 'add') {
                    newDay = new Date(day.getFullYear(), day.getMonth(), day.getDate() + parseInt(days));
                } else if (sign === 'subtract') {
                    newDay = new Date(day.getFullYear(), day.getMonth(), day.getDate() - parseInt(days));
                }

                day = newDay;
                if (!this.dayIsDisabled(newDay)) {
                    break;
                }
            }

            this.setFocus(newDay);
        },

        /**
         * Abstand des fokussierten Tages zum Wochenanfang (fuer Pos1 / Ende)
         */
        getWeekdayOffset: function () {
            var day = this._dFocus || this.getDate() || new Date();

            return (day.getDay() - this._o.firstDay + 7) % 7;
        },

        /**
         * Fokus um einen Monat verschieben (Bild auf / Bild ab)
         */
        adjustMonth: function (sign) {
            var day = this._dFocus || this.getDate() || new Date(),
                year = day.getFullYear(),
                month = day.getMonth() + (sign === 'add' ? 1 : -1),
                daysInTargetMonth = new Date(year, month + 1, 0).getDate();

            this.setFocus(new Date(year, month, Math.min(day.getDate(), daysInTargetMonth)));
        },

        adjustCalendars: function () {
            this.calendars[0] = adjustCalendar(this.calendars[0]);
            for (var c = 1; c < this._o.numberOfMonths; c++) {
                this.calendars[c] = adjustCalendar({
                    month: this.calendars[0].month + c,
                    year: this.calendars[0].year,
                });
            }
            this.draw();
        },

        /**
         * change view to a specific month (zero-index, e.g. 0: January)
         */
        gotoMonth: function (month) {
            if (!isNaN(month)) {
                this.calendars[0].month = parseInt(month, 10);
                this.adjustCalendars();
            }
        },

        nextMonth: function () {
            this.calendars[0].month++;
            this.adjustCalendars();
        },

        prevMonth: function () {
            this.calendars[0].month--;
            this.adjustCalendars();
        },

        /**
         * change view to a specific full year (e.g. "2012")
         */
        gotoYear: function (year) {
            if (!isNaN(year)) {
                this.calendars[0].year = parseInt(year, 10);
                this.adjustCalendars();
            }
        },

        /**
         * change the minDate
         */
        setMinDate: function (value) {
            if (value instanceof Date) {
                setToStartOfDay(value);
                this._o.minDate = value;
                this._o.minYear = value.getFullYear();
                this._o.minMonth = value.getMonth();
            } else {
                this._o.minDate = defaults.minDate;
                this._o.minYear = defaults.minYear;
                this._o.minMonth = defaults.minMonth;
                this._o.startRange = defaults.startRange;
            }

            this.draw();
        },

        /**
         * change the maxDate
         */
        setMaxDate: function (value) {
            if (value instanceof Date) {
                setToStartOfDay(value);
                this._o.maxDate = value;
                this._o.maxYear = value.getFullYear();
                this._o.maxMonth = value.getMonth();
            } else {
                this._o.maxDate = defaults.maxDate;
                this._o.maxYear = defaults.maxYear;
                this._o.maxMonth = defaults.maxMonth;
                this._o.endRange = defaults.endRange;
            }

            this.draw();
        },

        setStartRange: function (value) {
            this._o.startRange = value;
        },

        setEndRange: function (value) {
            this._o.endRange = value;
        },

        /**
         * refresh the HTML
         */
        draw: function (force) {
            if (!this._v && !force) {
                return;
            }
            var opts = this._o,
                minYear = opts.minYear,
                maxYear = opts.maxYear,
                minMonth = opts.minMonth,
                maxMonth = opts.maxMonth,
                html = '',
                randId;

            if (this._y <= minYear) {
                this._y = minYear;
                if (!isNaN(minMonth) && this._m < minMonth) {
                    this._m = minMonth;
                }
            }
            if (this._y >= maxYear) {
                this._y = maxYear;
                if (!isNaN(maxMonth) && this._m > maxMonth) {
                    this._m = maxMonth;
                }
            }

            randId =
                'pika-title-' +
                Math.random()
                    .toString(36)
                    .replace(/[^a-z]+/g, '')
                    .substr(0, 2);

            for (var c = 0; c < opts.numberOfMonths; c++) {
                html +=
                    '<div class="pika-lendar">' +
                    renderTitle(
                        this,
                        c,
                        this.calendars[c].year,
                        this.calendars[c].month,
                        this.calendars[0].year,
                        randId,
                    ) +
                    this.render(this.calendars[c].year, this.calendars[c].month, randId) +
                    '</div>';
            }

            var previouslyFocused = this.getActiveElement(),
                focusTarget = null;

            if (previouslyFocused && previouslyFocused.classList && this.el.contains(previouslyFocused)) {
                if (previouslyFocused.classList.contains('pika-day')) {
                    focusTarget = 'day';
                } else if (previouslyFocused.classList.contains('pika-prev')) {
                    focusTarget = '.pika-prev';
                } else if (previouslyFocused.classList.contains('pika-next')) {
                    focusTarget = '.pika-next';
                } else if (previouslyFocused.classList.contains('pika-today')) {
                    focusTarget = '.pika-today';
                }
            }

            this.el.innerHTML = html;
            this.syncRovingTabindex();

            if (focusTarget) {
                var elementToFocus =
                    focusTarget === 'day'
                        ? this.el.querySelector('button.pika-day[tabindex="0"]')
                        : this.el.querySelector(focusTarget);

                if (!elementToFocus || elementToFocus.disabled) {
                    elementToFocus = this.el.querySelector('button.pika-day[tabindex="0"]');
                }

                if (elementToFocus) {
                    elementToFocus.focus();
                }
            }

            if (opts.bound) {
                if (opts.field.type !== 'hidden') {
                    sto(function () {
                        opts.trigger.focus();
                    }, 1);
                }
            }

            if (typeof this._o.onDraw === 'function') {
                this._o.onDraw(this);
            }

            if (opts.bound) {
                // let the screen reader user know to use arrow keys
                opts.field.setAttribute('aria-label', opts.ariaLabel);
            }
        },

        syncRovingTabindex: function () {
            var buttons = this.el.querySelectorAll('button.pika-day');
            if (!buttons.length) {
                return;
            }

            var active =
                this.el.querySelector('.is-focus > button.pika-day') ||
                this.el.querySelector('.is-selected > button.pika-day');

            if (!active) {
                for (var i = 0; i < buttons.length; i++) {
                    if (buttons[i].getAttribute('aria-disabled') !== 'true') {
                        active = buttons[i];
                        break;
                    }
                }
            }

            if (!active) {
                active = buttons[0];
            }

            for (var j = 0; j < buttons.length; j++) {
                buttons[j].setAttribute('tabindex', buttons[j] === active ? '0' : '-1');
            }
        },

        focusFirstSelectableDay: function () {
            var day =
                this.el.querySelector('button.pika-day[tabindex="0"]:not([aria-disabled="true"])') ||
                this.el.querySelector('button.pika-day:not([aria-disabled="true"])');

            if (!day) {
                return false;
            }

            day.focus();

            return this.getActiveElement() === day;
        },

        adjustPosition: function () {
            var field,
                pEl,
                width,
                height,
                viewportWidth,
                viewportHeight,
                scrollTop,
                left,
                top,
                clientRect,
                leftAligned,
                bottomAligned;

            if (this._o.container) return;

            this.el.style.position = 'absolute';

            field = this._o.trigger;
            pEl = field;
            width = this.el.offsetWidth;
            height = this.el.offsetHeight;
            viewportWidth = window.innerWidth || document.documentElement.clientWidth;
            viewportHeight = window.innerHeight || document.documentElement.clientHeight;
            scrollTop = window.pageYOffset || document.body.scrollTop || document.documentElement.scrollTop;
            leftAligned = true;
            bottomAligned = true;

            if (typeof field.getBoundingClientRect === 'function') {
                clientRect = field.getBoundingClientRect();
                left = clientRect.left + window.pageXOffset;
                top = clientRect.bottom + window.pageYOffset;
            } else {
                left = pEl.offsetLeft;
                top = pEl.offsetTop + pEl.offsetHeight;
                while ((pEl = pEl.offsetParent)) {
                    left += pEl.offsetLeft;
                    top += pEl.offsetTop;
                }
            }

            // default position is bottom & left
            if (
                (this._o.reposition && left + width > viewportWidth) ||
                (this._o.position.indexOf('right') > -1 && left - width + field.offsetWidth > 0)
            ) {
                left = left - width + field.offsetWidth;
                leftAligned = false;
            }
            if (
                (this._o.reposition && top + height > viewportHeight + scrollTop) ||
                (this._o.position.indexOf('top') > -1 && top - height - field.offsetHeight > 0)
            ) {
                top = top - height - field.offsetHeight;
                bottomAligned = false;
            }

            this.el.style.left = left + 'px';
            this.el.style.top = top + 'px';

            addClass(this.el, leftAligned ? 'left-aligned' : 'right-aligned');
            addClass(this.el, bottomAligned ? 'bottom-aligned' : 'top-aligned');
            removeClass(this.el, !leftAligned ? 'left-aligned' : 'right-aligned');
            removeClass(this.el, !bottomAligned ? 'bottom-aligned' : 'top-aligned');
        },

        /**
         * render HTML for a particular month
         */
        render: function (year, month, randId) {
            var opts = this._o,
                now = new Date(),
                days = getDaysInMonth(year, month),
                before = new Date(year, month, 1).getDay(),
                data = [],
                row = [];
            setToStartOfDay(now);
            if (opts.firstDay > 0) {
                before -= opts.firstDay;
                if (before < 0) {
                    before += 7;
                }
            }
            var previousMonth = month === 0 ? 11 : month - 1,
                nextMonth = month === 11 ? 0 : month + 1,
                yearOfPreviousMonth = month === 0 ? year - 1 : year,
                yearOfNextMonth = month === 11 ? year + 1 : year,
                daysInPreviousMonth = getDaysInMonth(yearOfPreviousMonth, previousMonth);
            var cells = days + before,
                after = cells;
            while (after > 7) {
                after -= 7;
            }
            cells += 7 - after;
            var isWeekSelected = false;
            for (var i = 0, r = 0; i < cells; i++) {
                var day = new Date(year, month, 1 + (i - before)),
                    isSelected = isDate(this._d) ? compareDates(day, this._d) : false,
                    isFocus = isDate(this._dFocus) ? compareDates(day, this._dFocus) : false,
                    isToday = compareDates(day, now),
                    hasEvent = opts.events.indexOf(day.toDateString()) !== -1 ? true : false,
                    isEmpty = i < before || i >= days + before,
                    dayNumber = 1 + (i - before),
                    monthNumber = month,
                    yearNumber = year,
                    isStartRange = opts.startRange && compareDates(opts.startRange, day),
                    isEndRange = opts.endRange && compareDates(opts.endRange, day),
                    isInRange = opts.startRange && opts.endRange && opts.startRange < day && day < opts.endRange,
                    isDisabled =
                        (opts.minDate && day < opts.minDate) ||
                        (opts.maxDate && day > opts.maxDate) ||
                        (opts.disableWeekends && isWeekend(day)) ||
                        (opts.disableDayFn && opts.disableDayFn(day)),
                    weekend = isWeekend(day),
                    isInPast = day < opts.minDate,
                    i18n = opts.i18n,
                    firstDay = opts.firstDay;

                if (isEmpty) {
                    if (i < before) {
                        dayNumber = daysInPreviousMonth + dayNumber;
                        monthNumber = previousMonth;
                        yearNumber = yearOfPreviousMonth;
                    } else {
                        dayNumber = dayNumber - days;
                        monthNumber = nextMonth;
                        yearNumber = yearOfNextMonth;
                    }
                }

                var dayConfig = {
                    self: this._o,
                    firstDay: firstDay,
                    day: dayNumber,
                    month: monthNumber,
                    year: yearNumber,
                    hasEvent: hasEvent,
                    isWeekend: weekend,
                    isSelected: isSelected,
                    isFocus: isFocus,
                    isToday: isToday,
                    isDisabled: isDisabled,
                    isEmpty: isEmpty,
                    isStartRange: isStartRange,
                    isEndRange: isEndRange,
                    isInRange: isInRange,
                    isInPast: isInPast,
                    i18n: i18n,
                    showDaysInNextAndPreviousMonths: opts.showDaysInNextAndPreviousMonths,
                    enableSelectionDaysInNextAndPreviousMonths: opts.enableSelectionDaysInNextAndPreviousMonths,
                };

                if (opts.pickWholeWeek && isSelected) {
                    isWeekSelected = true;
                }

                row.push(renderDay(dayConfig));

                if (++r === 7) {
                    if (opts.showWeekNumber) {
                        row.unshift(renderWeek(i - before, month, year));
                    }
                    data.push(renderRow(row, opts.isRTL, opts.pickWholeWeek, isWeekSelected));
                    row = [];
                    r = 0;
                    isWeekSelected = false;
                }
            }
            return renderTable(opts, data, randId);
        },

        isVisible: function () {
            return this._v;
        },

        isFocus: function () {
            return this._f;
        },

        show: function () {
            if (!this.isVisible()) {
                this._v = true;
                this.draw();
                addEvent(document, 'focus', this.isFocus);
                removeClass(this.el, 'is-hidden');
                if (this._o.bound) {
                    addEvent(document, 'click', this._onClick);
                    this.adjustPosition();
                }
                if (typeof this._o.onOpen === 'function') {
                    this._o.onOpen.call(this);
                }
            }
        },

        hide: function () {
            var v = this._v;
            if (v !== false) {
                if (this._o.bound) {
                    removeEvent(document, 'click', this._onClick);
                }
                this.el.style.position = 'static'; // reset
                this.el.style.left = 'auto';
                this.el.style.top = 'auto';
                addClass(this.el, 'is-hidden');
                this._v = false;
                if (v !== undefined && typeof this._o.onClose === 'function') {
                    this._o.onClose.call(this);
                }
            }
        },

        /**
         * GAME OVER
         */
        destroy: function () {
            var opts = this._o;

            this.hide();
            removeEvent(this.el, 'click', this._onActivate, true);
            removeEvent(this.el, 'focusin', this._onFocusIn, true);
            removeEvent(this.el, 'change', this._onChange);

            if (opts.keyboardInput) {
                removeEvent(this.el, 'keydown', this._onKeyDown, true);
            }
            if (opts.field) {
                removeEvent(opts.field, 'change', this._onInputChange);
                if (opts.bound) {
                    removeEvent(opts.trigger, 'click', this._onInputClick);
                    removeEvent(opts.trigger, 'focus', this._onInputFocus);
                    removeEvent(opts.trigger, 'blur', this._onInputBlur);
                }
            }
            if (this.el.parentNode) {
                this.el.parentNode.removeChild(this.el);
            }
        },

        getActiveElement: function (root) {
            if (root === undefined) {
                root = document;
            }
            var activeEl = root.activeElement;

            if (!activeEl) {
                return null;
            }

            if (activeEl.shadowRoot) {
                return this.getActiveElement(activeEl.shadowRoot);
            } else {
                return activeEl;
            }
        },

        getQuerySelector: function (selectors) {
            return document.querySelector('otb-component').shadowRoot.querySelector(selectors);
        },
    };

    return Pikaday;
});
